import { type AnalyticsEvent, trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { useDebounceEffect } from '../../hooks';
import { Multiselect, TailwindButton } from '../../tailwind';
import { RippleRoot } from '../../tailwind/RippleRoot';

interface CoreApiMultiselectProps<T> {
  loading?: boolean;
  options: T[];
  selected: string[];
  className?: string;
  getOptionId: (option: T) => string;
  getOptionLabel: (option: T) => string;
  onSearchTermChange: (term: string) => void;
  onSelectedChange: (selected: string[]) => void;
  renderOption?: (option: T) => React.ReactNode;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
  isOptionDisabled?: (option: T) => boolean;
}

export const MultiSelectDropdown = <T,>({
  loading,
  options,
  selected,
  className,
  getOptionId,
  getOptionLabel,
  onSearchTermChange,
  onSelectedChange,
  renderOption,
  disabled,
  analyticsEvent,
  isOptionDisabled,
}: CoreApiMultiselectProps<T>) => {
  const { common } = useAutoneTranslation();

  const selectedOptions = useRef<T[]>([]);

  const getSelectableOptions = useCallback(() => {
    return isOptionDisabled
      ? options.filter((option) => !isOptionDisabled(option))
      : options;
  }, [options, isOptionDisabled]);

  const optionsCount = getSelectableOptions().length;
  const hasResults = options.length > 0;
  const allOptionsSelected =
    selected.length === optionsCount && optionsCount > 0;

  const handleSelectedChange = useCallback(
    (newSelected: string[]) => {
      onSelectedChange(newSelected);
      if (analyticsEvent) {
        trackAnalyticsEvent({
          ...analyticsEvent,
          properties: { values_descriptions: newSelected.join(', ') },
        });
      }
    },
    [analyticsEvent, onSelectedChange],
  );

  const handleClearAll = () => {
    onSelectedChange([]);
    if (analyticsEvent) {
      trackAnalyticsEvent({
        ...analyticsEvent,
        properties: {
          values_ids: '',
          values_descriptions: '',
        },
      });
    }
  };

  const handleSelectAll = useCallback(() => {
    const currentSelectableOptions = getSelectableOptions();
    // Keep all previously selected items and add all current selectable options
    const newSelected = Array.from(
      new Set([...selected, ...currentSelectableOptions.map(getOptionId)]),
    );

    onSelectedChange(newSelected);

    if (analyticsEvent) {
      trackAnalyticsEvent({
        ...analyticsEvent,
        properties: {
          values_ids: currentSelectableOptions.map(getOptionId).join(', '),
          values_descriptions: currentSelectableOptions
            .map(getOptionLabel)
            .join(', '),
        },
      });
    }
  }, [
    getSelectableOptions,
    selected,
    getOptionId,
    onSelectedChange,
    analyticsEvent,
    getOptionLabel,
  ]);

  const [localSearchTerm, setLocalSearchTerm] = useState('');

  useDebounceEffect(
    () => {
      onSearchTermChange(localSearchTerm);
    },
    [localSearchTerm, onSearchTermChange],
    300,
  );

  /**
   * We need to persist currently selected options so that when new options
   * come in, we can still display the existing options in the badges properly.
   */
  useEffect(() => {
    const selectedIdsMap = new Map(selected.map((id) => [id, true]));
    const currOptionsWithoutDeselected = selectedOptions.current.filter(
      (option) => selectedIdsMap.has(getOptionId(option)),
    );
    const newSelectedOptions = options.filter((option) =>
      selectedIdsMap.has(getOptionId(option)),
    );
    selectedOptions.current = Array.from(
      new Set([...currOptionsWithoutDeselected, ...newSelectedOptions]),
    );
  }, [selected, options]);

  return (
    <Multiselect.Root
      selected={selected}
      onSelectedChange={handleSelectedChange}
      disabled={disabled}
    >
      <Multiselect.Input
        className={className}
        loading={loading}
        value={localSearchTerm}
        options={[...options, ...selectedOptions.current]}
        maxBadges={2}
        onChange={(e) => setLocalSearchTerm(e.target.value)}
        getOptionId={getOptionId}
        getOptionLabel={getOptionLabel}
      />
      <Multiselect.PopoverContent
        dialog
        className="flex flex-col overflow-y-scroll"
        side={'bottom'}
      >
        {hasResults && (
          <div className="flex flex-row items-center p-1 border-b border-grey-10">
            <TailwindButton
              className="w-full rounded-sm"
              variant={'ghost'}
              color={'primary'}
              onClick={allOptionsSelected ? handleClearAll : handleSelectAll}
            >
              {allOptionsSelected
                ? common.withValue('dropdowns.clear-all', {
                    value: optionsCount,
                  })
                : common.withValue('dropdowns.select-all', {
                    value: optionsCount,
                  })}
            </TailwindButton>
          </div>
        )}
        <div className="flex flex-col w-full h-full min-h-0 overflow-y-scroll">
          <div className="flex flex-col">
            {hasResults ? (
              options.map((option) =>
                renderOption ? (
                  <Fragment key={getOptionId(option)}>
                    {renderOption(option)}
                  </Fragment>
                ) : (
                  <Multiselect.Item
                    key={getOptionId(option)}
                    optionId={getOptionId(option)}
                    className="relative flex flex-row gap-2 items-center justify-start w-full px-3 py-2 text-grey-100 h-fit hover:cursor-pointer hover:bg-primary-5 data-[selected=true]:bg-primary-10"
                  >
                    <Multiselect.ItemCheckbox />
                    <span className="text-left whitespace-normal">
                      {getOptionLabel(option)}
                    </span>
                    <RippleRoot className="bg-primary-80" />
                  </Multiselect.Item>
                ),
              )
            ) : (
              <div className="flex items-center justify-center w-full p-4 text-sm text-grey-80 font-medium">
                {common('no-results')}
              </div>
            )}
          </div>
        </div>
      </Multiselect.PopoverContent>
    </Multiselect.Root>
  );
};
